// Real-time order-status stream via Server-Sent Events.
//
// Any page that renders orders (Orders tab, dispatcher board, maps, KPI tiles)
// mounts this hook once and receives push updates the moment a driver marks
// an order Delivered or Failed on the Driver App — no polling delay, no
// manual refresh.
//
// Backend fans out per workspace: users only receive updates for orders in
// their own region. Super Admin viewing "All" gets every event.
//
// Usage:
//   useOrderEvents((update) => {
//     setOrders((prev) => prev.map((o) => o.id === update.id ? { ...o, ...update } : o));
//   });

import { useEffect, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * @param {(update: {id: string, status: string, region: string, ...}) => void} onUpdate
 * @param {object} opts
 * @param {string} [opts.region] - Explicit workspace ("all" | "south_fl" | "central_fl"). Defaults to the value in localStorage("pr_workspace") so the hook auto-tracks the active workspace switcher.
 * @param {boolean} [opts.enabled=true] - Turn the stream off entirely (e.g. when user is logged out).
 */
export default function useOrderEvents(onUpdate, opts = {}) {
  const cbRef = useRef(onUpdate);
  cbRef.current = onUpdate;

  useEffect(() => {
    if (opts.enabled === false) return;
    const token = localStorage.getItem("pr_token");
    if (!token) return;

    const region = opts.region || localStorage.getItem("pr_workspace") || "";
    const qs = new URLSearchParams({ token });
    if (region && region !== "all") qs.set("region", region);

    const url = `${BACKEND_URL}/api/orders/events?${qs.toString()}`;
    const es = new EventSource(url);

    es.addEventListener("order_update", (ev) => {
      try {
        const data = JSON.parse(ev.data);
        cbRef.current?.(data);
      } catch (err) {
        // Malformed event — log and skip; we'll get the next one.
        console.warn("[useOrderEvents] parse failed:", err);
      }
    });

    // Ready + error handlers are intentionally quiet — EventSource auto-reconnects
    // on error (with default 3-second backoff), so we let the browser handle it.
    es.addEventListener("ready", () => {});
    es.onerror = (err) => {
      // Common when the tab is put to sleep / laptop lid closes; EventSource
      // will reopen on its own. Log at debug so it doesn't spam production.
      if (es.readyState === EventSource.CLOSED) {
        console.debug("[useOrderEvents] stream closed:", err);
      }
    };

    return () => es.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.region, opts.enabled]);
}
